'use client';

import React, { useEffect, useState, useMemo } from 'react';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useProspectSearch, useProspectSelection } from 'src/context/prospects';
import { useSite } from 'src/context/providers/SiteProvider';
import {
  DataGridPremium,
  GridColumnMenuProps,
  GridFilterModel,
  GridRowId,
  GridRowSelectionModel,
  useGridApiRef,
  GridColumnMenu,
} from '@mui/x-data-grid-premium';
import {
  NewTableToolbar,
  TableCheckbox,
  TableFooter,
  TableLoadingIndicator,
  TableSettings,
  NewTablePrimaryHeader,
  NewTableControls,
  createProviderNameColumn,
  TableColumn,
} from 'src/components';
import useLocalStorage from 'src/hooks/system/useLocalStorage';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import { useGetReferringOutQuery, useGetMappingsQuery } from 'src/api/redux';

import { useParams } from 'next/navigation';
import {
  PrimaryTableContainer,
  InnerContainer,
  baseColumns,
} from '../components';
import { getTreatingReferringExport } from 'src/api/affiliations/affiliationsExportApi';
const ReferringTable = () => {
  const {
    id,
    type: location,
    view,
  } = useParams<{
    id: string;
    type: string;
    view: string;
  }>();
  const log = useLogging();
  const apiRef = useGridApiRef();
  const { entityDetails } = useProfile();
  const auth = useAuthorization();

  const { state } = useSite();

  const { setNotification } = useNotification();
  const { prospectVolumeType } = useProspectSearch();

  const [columnVisibilityModel, setColumnVisibilityModel] = useLocalStorage(
    'referringTable',
    {
      name: true,
      crm_links: true,
      specialty: true,
      rep_name: false,
      tags: false,
      provider_id: false,
      address: false,
      street_address: false,
      secondary_addresses_v2: false,
      city: false,
      state: false,
      zip_code: false,
      affiliations: false,
      groups: false,
      actions: false,
      phone: false,
      email: false,
      systems: false,
    }
  );

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [isExporting, setIsExporting] = useState(false);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [sort, setSort] = useState({
    field: null,
    order: null,
    sort_search: null,
    sort_value: null,
  });

  const queryParams = {
    providerId: id,
    sort: sort?.field,
    order: sort?.order,
    sort_search: sort?.sort_search,
    sort_value: sort?.sort_value,
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
    q: state?.siteProvider[location]?.search,
    volume_type: state?.siteProvider[location]?.selectedType,
    dateRange: state?.siteProvider[location]?.selectedDate,
  };

  const { data: referringData, isLoading } = useGetReferringOutQuery(
    queryParams,
    { skip: !id }
  );

  // Get all mappings to determine if CRM column should be visible
  const { data: allMappings } = useGetMappingsQuery({
    sort: 'label',
    order: 'asc',
    page: 0,
    pageSize: 100,
  });

  const _crmTypes = useMemo(() => {
    return Array.from(
      new Set(
        allMappings?.results
          ?.filter((mapping) => mapping.enabled)
          .map((mapping) => mapping.crm?.toLowerCase())
      )
    );
  }, [allMappings]);

  // Update the new prospects state when the selection model changes
  const {
    state: [, setNewProspects],
  } = useProspectSelection();

  useEffect(() => {
    if (state?.siteProvider[location]?.search) {
      if (paginationModel.page !== 0) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
    }
  }, [state?.siteProvider[location]?.search]);

  useEffect(() => {
    if (!apiRef.current?.getSelectedRows) return;
    setNewProspects(Array.from(apiRef.current.getSelectedRows().values()));

    return () => setNewProspects([]);
  }, [selectionModel]);

  const enhancedRows = useMemo(() => {
    if (!referringData?.results) return [];

    return referringData.results?.map((row) => {
      return row;
    });
  }, [referringData]);

  const handleExportList = (type: string) => {
    const columns = Object.entries(columnVisibilityModel)
      .filter(([key, value]) => value === true && isNaN(Number(key)))
      .map(([key]) => key)
      .concat(['volume']);
    setIsExporting(true);

    const fileName = `${entityDetails?.name}_AffiliationsReferringView`;

    try {
      const res = getTreatingReferringExport({
        searchParams: queryParams,
        tableType: 'referring',
        fileType: type,
        fileName,
        columns,
      });

      if (!res) return;
      setNotification({
        title: 'Success',
        message:
          'Export initiated - You will receive an email when it is ready for download',
        type: 'success',
      });
      log.event('Export', {
        type: 'affiliations',
        source: 'All Affiliated - Referring Out',
        columns,
        count: referringData?.count,
      });
    } catch (err: any) {
      log.exception(`Error exporting All Affiliated - Referring Out: ${err}`);

      setNotification({
        title: 'Error',
        message: err?.error_message || 'Error exporting, please try again.',
        type: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleSortChange = (model) => {
    let newField = 'name';
    let sort_search = '';
    let sort_value = '';

    if (model.length) {
      const field = model?.[0]?.field;
      if (!field) return;

      newField =
        isNaN(Number(field)) && field !== 'volume' ? field : 'saved_search';

      if (newField === 'saved_search') {
        sort_search = field !== 'volume' ? field : '';
        sort_value = prospectVolumeType;
      }
    }

    setSort({
      field: newField || 'name',
      order: model.length ? model[0].sort : 'desc',
      sort_search,
      sort_value,
    });
  };

  const handlePageChange = (e, newPage) => {
    setPaginationModel((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (e) => {
    setPaginationModel((prev) => ({
      ...prev,
      pageSize: parseInt(e.target.value, 10),
      page: 0,
    }));
  };

  const userCanExport = auth.hasPermission('export.site_providers.all');

  const dynamicColumns = useMemo(() => {
    const shouldRouteToReferrals =
      view === 'affiliations' && ['center', 'clinic'].includes(location);

    // Filter out the 'name' field column from baseColumns
    const otherColumns = baseColumns.filter((col) => col.field !== 'name');

    return [
      createProviderNameColumn('name', 'Name', { shouldRouteToReferrals }),
      TableColumn?.tags('tags', 'Tags'),
      ...otherColumns,
    ];
  }, [view, location]);

  return (
    <PrimaryTableContainer>
      <InnerContainer>
        <DataGridPremium
          disableVirtualization
          apiRef={apiRef}
          rows={enhancedRows || []}
          rowCount={referringData?.count || 0}
          columns={dynamicColumns}
          scrollbarSize={0}
          hideFooter={true}
          checkboxSelection
          disableColumnMenu
          disableRowSelectionOnClick
          sx={{
            // remove border from table and only show overflow on x axis
            border: 'none',
            borderRadius: 0,
            '& .MuiDataGrid-main, & .MuiDataGrid-footerContainer': {
              backgroundColor: 'white',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflowY: 'scroll',
              overflowX: 'auto',
              marginRight: '-1rem', // hides the Y axis scrollbar
            },
            '& .MuiDataGrid-columnHeadersInner>div:first-of-type, & .MuiDataGrid-groupHeader, & .MuiDataGrid-columnHeader--emptyGroup':
              {
                // display: 'none !important',
                height: 'auto !important',
              },
            '& .MuiDataGrid-row:hover, & .Mui-hovered': {
              backgroundColor: 'rgba(245, 245, 245, 0.6)',
              '&.Mui-hovered': {
                backgroundColor: 'rgba(229, 229, 229, 0.6)',
                transition: 'background-color 0.3s ease-in-out',
              },
            },
          }}
          initialState={{
            columns: { columnVisibilityModel },
            pagination: { paginationModel: { page: 0, pageSize: 50 } },
          }}
          slots={{
            toolbar: NewTableToolbar,
            baseCheckbox: TableCheckbox,
            columnMenu: CustomColumnMenu,
            ...TableSettings.slots,
          }}
          slotProps={{
            columnsManagement: {
              disableShowHideToggle: true,
            },
            toolbar: {
              variant: 'primary',
              headerSlots: {
                slot1: <NewTablePrimaryHeader title="Referring Out" />,
                slot2: (
                  <NewTableControls
                    columns={dynamicColumns}
                    columnVisibilityModel={columnVisibilityModel}
                    setColumnVisibilityModel={setColumnVisibilityModel}
                    onExport={handleExportList}
                    isExporting={isExporting}
                    showExportButton={userCanExport}
                  />
                ),
              },
            },
          }}
          // Pagination
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          // Sorting
          sortingMode="server"
          onSortModelChange={handleSortChange}
          // Filtering
          filterMode="server"
          onFilterModelChange={setFilterModel}
          filterModel={filterModel}
          // Column visibility
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          // Rows
          rowHeight={75}
          onRowSelectionModelChange={setSelectionModel}
          rowSelectionModel={selectionModel}
        />
        {isLoading && <TableLoadingIndicator />}
      </InnerContainer>
      <TableFooter
        page={paginationModel.page}
        pageSize={paginationModel.pageSize}
        rowCount={referringData?.count || 0}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </PrimaryTableContainer>
  );
};

const CustomColumnMenu = (props: GridColumnMenuProps) => {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        columnMenuFilterItem: null,
        columnMenuAggregationItem: null,
        columnMenuGroupingItem: null,
      }}
    />
  );
};

export default ReferringTable;
